import { useCallback, useState } from 'react';
import { DESIGN_WIDTH } from './geometry';

/**
 * Measures the stage and returns the factor from design pixels (the 1200-wide frame the scenes are
 * authored in) to real pixels. `null` until the stage has been laid out — the server render and the
 * first client render — so the layers that depend on it never render with a guess.
 *
 * A zero-width stage (jsdom, a hidden tab) counts as scale 1: nothing is visible then anyway, and the
 * real size arrives as soon as the stage is shown.
 */
export function useStageScale(): [
  ref: (element: HTMLElement | null) => void | (() => void),
  scale: number | null,
] {
  const [scale, setScale] = useState<number | null>(null);

  const ref = useCallback((element: HTMLElement | null) => {
    if (!element) return undefined;
    const measure = (): void => {
      const width = element.clientWidth;
      setScale(width > 0 ? width / DESIGN_WIDTH : 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return [ref, scale];
}
